import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, Select, StatusPill, Table, EmptyState, useToast,
} from '../../../design-system';
import AgentMachinePanel from '../agent/AgentMachinePanel';
import TallyOpenStrategy from './TallyOpenStrategy';
import TallyDataPath from './TallyDataPath';
import TallyScanDiagnostics from './TallyScanDiagnostics';
import AgentInstallHelp from '../client/AgentInstallHelp';
import '../../styles/TallyPanel.css';
import { invalidateTallyNavStatus } from '../../../lib/tallyNavStatus';

// Which companies exist in the firm's Tally, and which of them are clients yet.
//
// ⚠️ WHY THIS EXISTS AT ALL. Connecting Tally lived only inside a client
// record, which made the most useful question in the module unaskable: "we keep
// two hundred client companies in Tally — which of them aren't set up in the
// software yet?" Answering that from inside a client means opening a client to
// find out which clients are missing. It is the wrong level.
//
// ⚠️ THIS IS THE SETUP HALF OF THE FIRM'S TALLY PAGE. The other half —
// components/tally/TallyBooks.jsx — is what you do with a company once it is
// bound: its ledgers, its entries, its reports, its match queue. Two tabs of
// one page rather than two pages, because they are the same errand at two
// stages ("get this company in" then "read it") and a firm moves between them
// constantly while onboarding.
//
// ⚠️ THE LEVELS ARE DIFFERENT QUESTIONS, NOT TWO PLACES TO DO ONE THING:
//
//   this tab      which COMPANIES exist in Tally, and which are clients yet
//   Books tab     read the books of any company already bound
//   client's tab  what shape THIS client is in (its financial position)
//
// A Tally company maps to a client; the ledgers inside it are that client's own
// customers. Confusing those two levels is what once turned a client's debtors
// into clients of the practice.

const STATUS = {
  connected: { variant: 'success', label: 'Connected' },
  client_exists: { variant: 'info', label: 'Client exists' },
  new: { variant: 'warning', label: 'Not in the software' },
};

function TallyCompanies() {
  const toast = useToast();

  const [agents, setAgents] = useState([]);
  // Where this machine's companies live. Blank means Tally's own configured
  // data directory, which is what almost every firm wants.
  const [dataPath, setDataPath] = useState('');
  // Set when the disk listing came back empty and the server fell back to the
  // open-companies-only request — see the banner below.
  const [degraded, setDegraded] = useState(false);
  // Which KIND of degraded: Tally answered about the open company and the folder
  // we named had no bearing on it. Different cause, different advice.
  const [pathIgnored, setPathIgnored] = useState(false);
  // What the scan is doing right now — a folder-by-folder read of a hundred
  // machines' worth of directories is a long silence otherwise.
  const [scanNote, setScanNote] = useState('');
  // Folders Tally could not read. Reported, never silently dropped: one
  // disconnected mapped drive out of a hundred just looks like a shorter list.
  const [folderFailures, setFolderFailures] = useState([]);
  // What Tally actually replied, per folder — the evidence, not a summary.
  const [diagnostics, setDiagnostics] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [connecting, setConnecting] = useState('');
  const [installOpen, setInstallOpen] = useState(false);

  const loadAgents = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/credentials/agents/mine');
      const list = res.data?.agents || [];
      setAgents(list);
      if (list.length === 1) setAgentId(list[0].id);
    } catch {
      toast.danger('Could not list the computers running the agent');
    }
    // toast is stable enough for this one-shot load; re-running on it would
    // refetch the machine list on every toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  // ⚠️ THE STORED FOLDER IS SHOWN BACK, and it was not. The path went in, was
  // saved on the machine and used by every job — and came back out of nothing,
  // so this box was empty on every page load. A firm could set `Y:\OfficeTally`,
  // have it working, and have no way to see that it was set, no way to correct a
  // typo except by retyping the whole thing, and no way to tell "not configured"
  // from "configured and I cannot see it".
  //
  // Keyed on the chosen machine, because the folder belongs to the machine: two
  // PCs in one firm can keep their companies in different places.
  const chosen = agents.find((a) => a.id === agentId);
  useEffect(() => {
    setDataPath(chosen?.tallyDataPath || '');
  }, [chosen?.id, chosen?.tallyDataPath]);

  /**
   * Ask the machine what companies it has, then ask the server which of them we
   * already know.
   *
   * Two calls rather than one: discovery is a queued job the agent answers when
   * it next polls, and the review is an ordinary query against our own database.
   * Folding them together would hold an HTTP request open for a poll interval.
   */
  const scan = async () => {
    if (!agentId) return;
    setScanning(true);
    setDegraded(false);
    setPathIgnored(false);
    setScanNote('');
    setFolderFailures([]);
    setDiagnostics([]);
    setRows([]);
    setSummary(null);
    setSelected(new Set());
    try {
      const { data } = await apiClient.post('/api/tally/discover', { agentInstallId: agentId, dataPath });

      // ⚠️ THE DEADLINE HAS TO SCALE WITH THE FOLDERS. A machine that files each
      // client separately is read in chunks of forty, chained — so a hundred and
      // twenty two folders is four jobs, each waiting a poll interval and then
      // making forty requests to Tally. A flat ninety seconds gave up partway
      // and blamed the agent for a scan that was working.
      const folderCount = data.folderCount || 0;
      setScanNote(folderCount
        ? `Reading ${folderCount} folder${folderCount === 1 ? '' : 's'} on that computer…`
        : '');
      const deadline = Date.now() + Math.max(90000, folderCount * 3000);
      let companies = null;
      let lastStatus = 'queued';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/api/tally/discover/${data.jobId}`);
        lastStatus = res.data.status;
        if (res.data.status === 'succeeded') {
          companies = res.data.companies || [];
          setDegraded(!!res.data.degraded);
          setPathIgnored(!!res.data.pathIgnored);
          setFolderFailures(res.data.folderFailures || []);
          setDiagnostics(res.data.diagnostics || []);
          break;
        }
        if (res.data.foldersLeft) {
          setScanNote(`Still reading — ${res.data.foldersLeft} folder(s) to go…`);
        }
        if (res.data.status === 'failed' || res.data.status === 'expired') {
          toast.danger(res.data.error || 'The agent could not reach Tally');
          break;
        }
        if (Date.now() > deadline) {
          // The same two-failures distinction the client tab makes: a job still
          // queued was never claimed, and Tally was never contacted at all.
          toast.danger(
            lastStatus === 'queued'
              ? 'Nothing on that machine picked this up — the Tally sync service is not running there.'
              : 'The agent asked Tally and got no answer. Is Tally open, with the gateway switched on?',
          );
          break;
        }
      }

      if (!companies) return;
      if (!companies.length) {
        // ⚠️ THE OLD COPY HERE WAS WRONG, and wrong in the direction that cost
        // a firm the whole feature: it said 'Tally has no company open — open
        // the ones you want to see', which taught people that this software can
        // only ever see open companies. It now reads the data directory, so an
        // empty answer means the FOLDER is wrong or unreadable, not that
        // nothing is open.
        toast.warning(
          dataPath
            ? `Tally found no companies in ${dataPath}. Check the path is exactly as Tally shows it, and that the drive is connected.`
            : 'Tally found no companies in that folder. If your companies are somewhere else, use “Use a different folder” above.',
        );
        return;
      }

      const review = await apiClient.post('/api/tally/companies/review', { companies });
      setRows(review.data.rows || []);
      setSummary(review.data.summary || null);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not scan that computer');
    } finally {
      setScanning(false);
      setScanNote('');
      // ⚠️ IN `finally`, NOT ON SUCCESS. The server saves the folder before it
      // queues anything, so a scan that then fails has still changed what is
      // stored — and the line saying what is saved has to agree with that, or a
      // failed scan leaves the screen describing the previous path.
      loadAgents();
    }
  };

  /**
   * Remove a machine from the firm's list.
   *
   * ⚠️ THE SAME ACT AS ON THE CLIENT'S TALLY TAB, AND IT BELONGS HERE MORE.
   * Managing the computer that runs Tally is a fact about the PRACTICE, not
   * about any one client — but "Forget this device" existed only inside a client
   * record, so repairing a machine meant opening some arbitrary client to get at
   * a control that had nothing to do with them. This page is where the firm's
   * machines are already listed and chosen.
   *
   * ⚠️ REVOKED, NEVER DELETED, and that is what makes it safe to offer: the row
   * is what an order was encrypted to and what the access log points at.
   * `registerAgent` upserts on the machine fingerprint, so setting the same
   * computer up again revives THIS row rather than leaving a second one behind.
   * Forgetting is therefore repair, not destruction.
   */
  const forgetMachine = async (agent) => {
    // eslint-disable-next-line no-alert
    const yes = window.confirm(
      `Remove "${agent.label}" from this firm?\n\n`
      + 'Nothing already synced is deleted, and setting the same computer up '
      + 'again brings it back. Until then it cannot be asked for anything.',
    );
    if (!yes) return;

    setScanning(true);
    try {
      await apiClient.post(`/api/credentials/agents/${agent.id}/forget`);
      const res = await apiClient.get('/api/credentials/agents/mine');
      const list = res.data?.agents || [];
      setAgents(list);
      // ⚠️ THE SELECTION GOES WITH IT. Left alone, the picker keeps a machine
      // that is no longer in the list and the next Scan targets a revoked id —
      // which fails in a way that reads as "Tally is broken".
      setAgentId(list.length === 1 ? list[0].id : '');
      setRows([]);
      setSummary(null);
      setSelected(new Set());
      setDiagnostics([]);
      toast.success('That computer has been removed. Setting it up again will bring it back.');
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not remove that computer');
    } finally {
      setScanning(false);
    }
  };

  const toggle = (name) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const importSelected = async () => {
    if (!selected.size) return;
    setImporting(true);
    try {
      const companies = rows.filter((r) => selected.has(r.name));
      // ⚠️ THE MACHINE TRAVELS WITH THE IMPORT. Creating the clients is only
      // half of it — a client with no connection reads nothing, and binding a
      // hundred of them by hand afterwards is the work this screen exists to
      // remove. The server cannot infer which computer these companies are on;
      // it is the one on screen.
      const { data } = await apiClient.post('/api/tally/companies/import', {
        companies,
        agentInstallId: agentId,
        connect: true,
      });
      const bits = [];
      if (data.created?.length) bits.push(`${data.created.length} client(s) created`);
      if (data.connected?.length) bits.push(`${data.connected.length} connected and syncing`);
      if (data.skipped?.length) bits.push(`${data.skipped.length} skipped`);
      if (data.connectFailures?.length) bits.push(`${data.connectFailures.length} could not be connected`);
      // ⚠️ A FAILED CONNECTION IS NAMED, not counted. On a hundred rows, "3 could
      // not be connected" is unactionable — the whole point of the count is
      // finding which three and why.
      if (data.connectFailures?.length) {
        const first = data.connectFailures.slice(0, 3)
          .map((f) => `${f.name}: ${f.reason}`).join('; ');
        toast.warning(`Import finished — ${bits.join(', ')}. ${first}`);
      } else if (data.skipped?.length) toast.warning(`Import finished — ${bits.join(', ')}`);
      else toast.success(`Import finished — ${bits.join(', ')}`);
      // Re-scan so the rows show their new state rather than the state that led
      // to the import.
      await scan();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not import those companies');
    } finally {
      setImporting(false);
    }
  };

  const connectRow = async (row) => {
    setConnecting(row.name);
    try {
      await apiClient.post('/api/tally/companies/connect', {
        clientId: row.clientId,
        agentInstallId: agentId,
        companyName: row.name,
        companyGuid: row.guid,
        booksFrom: row.booksFrom,
        // ⚠️ THE FOLDER THE COMPANY WAS FOUND IN. Without it a company in a
        // subfolder connects fine and then syncs nothing at all — Tally answers
        // an unknown company with an empty envelope, not an error.
        dataPath: row.dataPath || '',
      });
      toast.success(`${row.name} is now connected to ${row.clientName}. The first sync has started.`);
      invalidateTallyNavStatus();
      // Re-scan so the row shows what it IS now, not what led to the action.
      await scan();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not connect that company');
    } finally {
      setConnecting('');
    }
  };

  const disconnectRow = async (row) => {
    // eslint-disable-next-line no-alert
    const yes = window.confirm(
      `Disconnect "${row.name}" from ${row.connectedClientName || 'that client'}?

`
      + 'Nothing already mirrored is deleted. Connecting it again is a fresh setup, '
      + 'which re-records the client’s consent.',
    );
    if (!yes) return;

    setConnecting(row.name);
    try {
      await apiClient.patch(`/api/tally/connections/${row.connectionId}`, { status: 'revoked' });
      invalidateTallyNavStatus();
      toast.success(`${row.name} disconnected. You can now connect it to the right client.`);
      await scan();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not disconnect that company');
    } finally {
      setConnecting('');
    }
  };

  // ⚠️ TWO KINDS OF ROW ARE ACTIONABLE, NOT ONE. 'new' becomes a client and gets
  // bound; 'client_exists' is already a client and only needs binding. Offering
  // the tick-box on the first alone is what left the second — the commonest row
  // on a firm's second scan — reachable one at a time.
  const actionable = rows.filter((r) => r.status === 'new' || r.status === 'client_exists');
  const allPicked = actionable.length > 0 && actionable.every((r) => selected.has(r.name));
  const toggleAll = () => setSelected(allPicked ? new Set() : new Set(actionable.map((r) => r.name)));
  const pickedNew = rows.filter((r) => selected.has(r.name) && r.status === 'new').length;
  const pickedExisting = rows.filter((r) => selected.has(r.name) && r.status === 'client_exists').length;

  return (
    <>
      <Card>
        {agents.length === 0 ? (
          <>
            <p className="tally-warn">
              No computer has the agent installed yet. It has to run on the
              machine where Tally is open.
            </p>
            <Button onClick={() => setInstallOpen(true)}>Install it on this computer</Button>
          </>
        ) : (
          <>
            <Select
              label="Computer running Tally"
              value={agentId}
              onChange={(e) => { setAgentId(e.target.value); setRows([]); setSummary(null); }}
            >
              <option value="">Choose a computer…</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.label} (${a.version || 'unknown version'})`}
                </option>
              ))}
            </Select>

            {/* ⚠️ ONE COMPONENT, TWO HOSTS — the rule this codebase already
                follows for PlanChooser and the audit-log explorer. The client's
                Tally tab renders this same panel; passing `onForget` here is
                what makes the firm-level page a complete place to manage a
                machine rather than a read-only view of one. */}
            <AgentMachinePanel
              agent={chosen}
              busy={scanning}
              onReinstall={() => setInstallOpen(true)}
              onForget={forgetMachine}
            />

            {/* ⚠️ DISCOVERED, NOT TYPED. The agent reads this out of the
                machine's own tally.ini, so a working install needs no setup step
                and behaves identically on the next computer. A hand-entered
                `Y:\OfficeTally` is configuration that is right on exactly one
                PC and silently wrong everywhere else. */}
            <TallyDataPath
              agent={chosen}
              override={dataPath}
              onOverride={setDataPath}
            />

            <Button onClick={scan} disabled={!agentId || scanning} loading={scanning}>
              {scanning ? 'Asking Tally…' : 'Scan Tally'}
            </Button>

            {/* A folder-by-folder read of a hundred directories is a long
                silence otherwise, and silence is what makes people press the
                button again. */}
            {scanning && scanNote && <p className="tally-muted">{scanNote}</p>}

            {/* ⚠️ A FOLDER THAT COULD NOT BE READ IS NAMED. Out of a hundred and
                twenty two, one disconnected mapped drive produces a shorter list
                and nothing else — which reads as "those companies do not exist"
                rather than "we could not look". */}
            {folderFailures.length > 0 && (
              <p className="tally-warn">
                {folderFailures.length} folder(s) could not be read, so any companies
                in them are missing from the list below: {folderFailures.slice(0, 3).join('; ')}
                {folderFailures.length > 3 ? ` …and ${folderFailures.length - 3} more` : ''}
              </p>
            )}

            {/* ⚠️ THE EVIDENCE, not a summary of it. A scan that returns one
                company out of a hundred looks exactly like a firm that has one
                company, and no amount of reasoning from this side settles which
                it is — only Tally's actual reply does. */}
            <TallyScanDiagnostics diagnostics={diagnostics} />

            {/* ⚠️ THE ANSWER THAT DECIDES WHETHER ANY OF THIS CAN RUN
                UNATTENDED, and it was computed and shown to nobody. It belongs
                beside the machine picker because it is a fact about the machine,
                not about any one client. */}
            <TallyOpenStrategy agentId={agentId} companies={rows.map((r) => r.name)} />
          </>
        )}
      </Card>

      {/* ⚠️ THE FIRM MUST NOT BE LEFT TO CONCLUDE THAT IS ALL TALLY HAS. When
          the data-directory listing comes back empty the server falls back to
          the companies somebody happens to have open — which is one, on a
          machine holding a hundred. Silently showing that short list is exactly
          how this feature looked broken for a year. */}
      {degraded && (
        <Card>
          {/* ⚠️ TWO DEGRADED CASES, AND THE OLD COPY GAVE THE WRONG ADVICE FOR
              ONE OF THEM. "Check the folder path" is useless when the folder is
              precisely what Tally ignored — it sends somebody to re-type a path
              that was already right, conclude it is still broken, and re-type it
              again. Measured on TallyPrime Gold 6.x: every folder we name comes
              back byte-identical, so the path has no bearing on the answer. */}
          {pathIgnored ? (
            <p className="tally-warn">
              <strong>Tally ignored the folder and answered about the company that is open.</strong>{' '}
              We asked it about each of your company folders in turn and it gave
              the identical answer every time — so this list is whatever is
              currently open on that computer, not what you have on disk.
              This is a limit of Tally&apos;s gateway, not a wrong path: there is
              nothing to correct above, and re-typing the folder will not change
              it. Companies have to be enumerated another way.
            </p>
          ) : (
            <p className="tally-warn">
              <strong>This is only the companies currently open in Tally.</strong>{' '}
              The folder holding your company data could not be read, so what is
              listed below is not everything you have. Check the folder path above
              matches what Tally shows under Select Company, and that the drive is
              connected on that machine.
            </p>
          )}
        </Card>
      )}

      {rows.some((r) => r.nameMismatch) && (
        <Card>
          <p className="tally-warn">
            <strong>Check the highlighted rows.</strong> A Tally company is
            mirroring into a client with a different name. That is legitimate
            when a firm names a client differently from its company — and it is
            also what a wrong binding looks like, which quietly files one
            client’s books under another’s.
          </p>
        </Card>
      )}

      {summary && (
        <Card>
          <div className="tally-head">
            <div>
              <h3 className="tally-section-title">
                {summary.total === 1 ? '1 company' : `${summary.total} companies`} in Tally
              </h3>
              <p className="tally-muted">
                {summary.connected} already syncing ·{' '}
                {summary.clientExists === 1
                  ? '1 is a client but not connected'
                  : `${summary.clientExists} are clients but not connected`}{' '}
                · <strong>{summary.missing} not in the software</strong>
              </p>
            </div>
            {actionable.length > 0 && (
              <div className="tally-actions">
                {/* ⚠️ "ALL" IS THE ORDINARY CASE HERE, and ticking a hundred and
                    twenty two boxes by hand is not a confirmation, it is an
                    obstacle. Nothing is pre-ticked — every row still has to be
                    chosen — but choosing them all is one gesture. */}
                <Button variant="ghost" onClick={toggleAll} disabled={importing}>
                  {allPicked ? 'Clear selection' : `Select all ${actionable.length}`}
                </Button>
                <Button
                  onClick={importSelected}
                  loading={importing}
                  disabled={importing || !selected.size}
                >
                  {/* The label says what will HAPPEN to the rows picked, which is
                      two different things: a new company becomes a client and is
                      bound; one that is already a client is only bound. */}
                  {[
                    pickedNew ? `Create ${pickedNew} client${pickedNew === 1 ? '' : 's'}` : '',
                    pickedExisting ? `connect ${pickedExisting} existing` : '',
                  ].filter(Boolean).join(' & ') || 'Create clients'}
                </Button>
              </div>
            )}
          </div>

          {/* ⚠️ NOTHING IS PRE-TICKED, the same rule the party import now
              follows. Matching is by NAME — Tally's company list carries no PAN
              or GSTIN, because those live inside a company that would have to be
              open — so "Aarav Traders" and "Aarav Traders Pvt Ltd" are two
              strings and one company. A person confirms every row. */}
          <Table
            columns={[
              {
                key: 'pick',
                header: '',
                render: (r) => (r.status === 'new' || r.status === 'client_exists' ? (
                  <input
                    type="checkbox"
                    checked={selected.has(r.name)}
                    onChange={() => toggle(r.name)}
                    aria-label={r.status === 'new'
                      ? `Create a client for ${r.name}`
                      : `Connect ${r.name} to ${r.clientName}`}
                  />
                ) : null),
              },
              { key: 'name', header: 'Company in Tally', render: (r) => r.name },
              {
                key: 'status',
                header: 'Status',
                render: (r) => <StatusPill variant={STATUS[r.status].variant} label={STATUS[r.status].label} />,
              },
              {
                key: 'client',
                header: 'Client here',
                render: (r) => {
                  // ⚠️ FOR A CONNECTED ROW, WHAT MATTERS IS WHO IT IS BOUND TO —
                  // not who its name resembles. Showing the name match here read
                  // as a reassuring dash beside "Connected" while the company was
                  // in fact mirroring under another client entirely.
                  if (r.status === 'connected') {
                    const bound = `${r.connectedClientCode || ''} ${r.connectedClientName || 'unknown client'}`.trim();
                    return (
                      <span className={r.nameMismatch ? 'tally-bind-warn' : undefined}>
                        {bound}
                        {r.nameMismatch && (
                          <span className="tally-bind-note">
                            {' '}— different name from the company. Check this is the right client.
                          </span>
                        )}
                      </span>
                    );
                  }
                  return r.clientName ? `${r.clientCode || ''} ${r.clientName}`.trim() : '—';
                },
              },
              {
                key: 'action',
                header: '',
                render: (r) => {
                  // ⚠️ 'client_exists' is the commonest row on a real firm's
                  // scan, and it used to offer nothing — the page named the gap
                  // and then left the user to go and find that client, open its
                  // Tally tab, choose this machine again and retype the company
                  // name they were already looking at. The review knows both
                  // ends; joining them is one call.
                  if (r.status === 'connected') {
                    // Revoking is one-way by design — re-connecting runs the
                    // setup flow again and re-records consent. That is the right
                    // weight for undoing a binding that has been mirroring one
                    // client's books under another client's name.
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => disconnectRow(r)}
                        disabled={!!connecting}
                        loading={connecting === r.name}
                      >
                        Disconnect
                      </Button>
                    );
                  }
                  if (r.status !== 'client_exists') return null;
                  return (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => connectRow(r)}
                      disabled={!!connecting}
                      loading={connecting === r.name}
                    >
                      Connect
                    </Button>
                  );
                },
              },
            ]}
            data={rows}
            keyField="name"
          />
        </Card>
      )}

      {!summary && agents.length > 0 && (
        <EmptyState
          icon="📗"
          title="Nothing scanned yet"
          description="Pick the computer that runs Tally and scan it to see which companies are already clients."
        />
      )}

      {installOpen && (
        <AgentInstallHelp
          onClose={() => setInstallOpen(false)}
          onReady={() => { setInstallOpen(false); loadAgents(); }}
        />
      )}
    </>
  );
}

export default TallyCompanies;
